import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, message as notification, Modal } from 'antd'
import { RouteComponentProps } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ApplicationState } from 'src/store'
import { ButtonTitled, PageHeader } from 'src/components/Ant'
import {
  getFullRequest,
  setFullRequest,
  setUnderwayStatus
} from 'src/core/store/ducks/requests/actions'
import { IconEditRequestTitle } from 'src/components/atoms/edit-request-title'
import { Request, RequestUserDataStatus } from 'src/types'
import { SaveObservationRequest } from 'src/services/api/requests'
import SegmentControl, { Segments } from 'src/components/SegmentControl'
import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'
import { ContainerSegmentControl, WrapSpin } from './styles'
import NpsModal from './nps-modal'
import { shouldShowRDVButton } from '../utils'
import RDVButton from '../../components/RDVButton'
import Receipts from '../../components/Receipts'
import RequestHistoryButton from '../../components/request-history-button'
import RequestSummary from '../../components/RequestSummary'

interface Params {
  request_id: string
}

type SegmentKeys = 'receipts' | 'summary'

type SummarySegments = Segments<SegmentKeys>

function MyRequestsSummary({
  history,
  match: {
    params: { request_id }
  }
}: RouteComponentProps<Params>) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    requests,
    me: {
      user: { auditor, user_id, tenant_id, user_level_type }
    }
  } = useSelector((state: ApplicationState) => ({
    me: state.me,
    requests: state.requests
  }))

  const [npsModalVisible, setNpsModalVisible] = useState(false)

  const userDataNps = useMemo(
    () =>
      requests.fullRequest &&
      requests.fullRequest.request_user_data.find(
        userDataItem => userDataItem.traveler?.user_id === user_id
      ),
    [requests.fullRequest, user_id]
  )

  const policyRule = useReasonPolicyRule(
    requests.fullRequest
      ? requests.fullRequest.request_user_data?.[0].reason_id
      : -1
  )

  const isSuperAdmin = ['SUPER_ADMIN'].includes(user_level_type)
  const enableActions = requests.fullRequest?.allowed

  const nextStatus = useMemo<RequestUserDataStatus>(() => {
    if (requests.fullRequest) {
      const isRequester = requests.fullRequest.requester.user_id === user_id
      const isParticipant = requests.fullRequest.request_user_data.find(
        item => item.traveler.user_id === user_id
      )

      if (isParticipant) {
        switch (isParticipant.status) {
          case 'APPROVAL_REPROVED':
            return 'APPROVAL_PENDING'
          case 'CLOSING_REPROVED':
          case 'CONFERENCE_REPROVED':
          case 'UNDERWAY':
            return 'CONFERENCE_PENDING'
          default:
            return
        }
      }

      if (isRequester) {
        // approval is general, do not join
        const userData = requests.fullRequest.request_user_data.find(item =>
          [
            'APPROVAL_REPROVED',
            'CLOSING_REPROVED',
            'CONFERENCE_REPROVED',
            'UNDERWAY'
          ].includes(item.status)
        )

        if (userData) {
          switch (userData.status) {
            case 'APPROVAL_REPROVED':
              return 'APPROVAL_PENDING'
            case 'CLOSING_REPROVED':
            case 'CONFERENCE_REPROVED':
            case 'UNDERWAY':
              return 'CONFERENCE_PENDING'
          }
        }
      }
    }
  }, [requests.fullRequest, user_id])

  const [changingStatus, setChangingStatus] = useState<boolean>(false)
  const [current, setCurrent] = useState<SegmentKeys>('summary')
  const [saving, setSaving] = useState<boolean>(false)

  const enableTicketsServices = useCallback(
    ticketType => {
      if (!requests.fullRequest) {
        return false
      }

      if (requests.fullRequest.requester.user_id === user_id) {
        switch (requests.fullRequest.status) {
          case 'APPROVAL_REPROVED':
            return true
          case 'APPROVAL_PENDING':
          case 'UNDERWAY':
            return requests.fullRequest.request_tickets
              .filter(
                ({ request_ticket_type }) => request_ticket_type === ticketType
              )
              .some(({ status }) => status === 'EMISSION_CANCELED')
        }
      }
      return false
    },
    [requests.fullRequest, user_id]
  )

  const enableAirs = enableTicketsServices('AIR')
  const enableBudget = policyRule?.enable_budget
  const enableHotels = enableTicketsServices('HOTEL')
  const enableVehicles = enableTicketsServices('VEHICLE')

  async function saveRequest(request: Request) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.summary.change-observation-confirm'),
      onCancel() {},
      onOk: async () => {
        await SaveObservationRequest({
          organization_id: request.organization_id,
          request_id: request.request_id,
          obs: request.obs
        })
        setTimeout(() => {
          dispatch(getFullRequest(request.request_id, 'my_requests'))
        }, 100)
      },
      title: t('common.actions.delete.title')
    })
    return await new Promise(() => {})
  }

  const segments = useMemo<SummarySegments>(
    () => ({
      summary: {
        disabled: false,
        title: 'web.summary.title',
        renderContent: () => (
          <RequestSummary
            enableAdvancesEdit={enableActions}
            enableBillingEdit={enableActions}
            enableBudget={enableBudget}
            enableEditAirs={enableAirs}
            enableEditHotels={enableHotels}
            enableEditObservation={requests.fullRequest.status === 'UNDERWAY'}
            enableEditVehicles={enableVehicles}
            enableItinerariesEdit={enableActions}
            enableSharingEdit={isSuperAdmin ? isSuperAdmin : enableActions}
            originRequest="my_requests"
            request={requests.fullRequest}
            saveRequest={saveRequest}
          />
        )
      },
      receipts: {
        disabled: false,
        title: 'web.receipts.title',
        renderContent: () => <Receipts request={requests.fullRequest} />
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requests.fullRequest]
  )

  useEffect(() => {
    const castRequestId = Number(request_id)
    if (castRequestId) {
      dispatch(getFullRequest(castRequestId, 'my_requests'))
    } else {
      history.push('/my-requests')
    }
  }, [dispatch, history, request_id])

  useEffect(() => {
    if (requests.saveFullRequestLoading !== saving) {
      setSaving(requests.saveFullRequestLoading)

      if (!requests.saveFullRequestLoading && !requests.saveFullRequestError) {
        Modal.destroyAll()
        dispatch(getFullRequest(request_id, 'my_requests'))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.saveFullRequestLoading])

  useEffect(() => {
    if (requests.setStatusLoading !== changingStatus) {
      setChangingStatus(requests.setStatusLoading)

      if (!requests.setStatusLoading) {
        if (requests.setStatusError) {
          const content = requests.setStatusError

          setTimeout(() => {
            Modal.warning({
              content,
              title: t('common.attention')
            })
          }, 1000)
        } else {
          notification.success(
            t('common.push-notifications.request-conference-successful'),
            5
          )
          history.push('/my-requests')
        }

        Modal.destroyAll()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests.setStatusLoading])

  function handleAction() {
    const hasTicketsEmissionCanceled =
      requests.fullRequest.request_tickets.findIndex(
        ({ active, status }) => active && status === 'EMISSION_CANCELED'
      )

    // "web.receipts.modal-accept-change-has-tickets-canceled'
    const content = t(
      `web.receipts.modal-accept${enableActions ? '-change' : ''}${
        hasTicketsEmissionCanceled > -1 ? '-has-tickets-canceled' : ''
      }`
    )

    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content,
      okText: t('common.actions.yes'),
      onCancel() {},
      onOk: async () => {
        return new Promise(() => {
          dispatch(
            setUnderwayStatus({
              request_id,
              status: nextStatus
            })
          )
        }).catch(() => {})
      },
      title: t('common.are-you-sure')
    })
  }

  if (requests.fullRequestLoading) {
    return <WrapSpin />
  }

  if (requests.fullRequestError) {
    return <Alert message={requests.fullRequestError} showIcon type="error" />
  }

  if (!requests.fullRequest) {
    const message = t('common.response.unexpected-error')
    return <Alert message={message} showIcon type="error" />
  }

  const canAuditorEdit =
    auditor && [334081949181, 597990758089].includes(+tenant_id)

  const title = (
    <>
      {t('web.new-request.title-section-id-title', {
        id: requests.fullRequest.request_number,
        title: requests.fullRequest.title
      })}
      {canAuditorEdit && (
        <IconEditRequestTitle
          requestId={requests.fullRequest.request_id}
          organizationId={requests.fullRequest.organization_id}
          update={() =>
            dispatch(getFullRequest(Number(request_id), 'my_requests'))
          }
          title={requests.fullRequest.title}
        />
      )}
    </>
  )

  return (
    <>
      <NpsModal
        posSave={nps => {
          handleAction()
          setFullRequest({
            ...requests.fullRequest,
            request_user_data: requests.fullRequest.request_user_data.map(
              userItem => ({
                ...userItem,
                request_nps:
                  userDataNps.traveler.user_id === userItem.traveler.user_id
                    ? nps
                    : userItem.request_nps
              })
            )
          })
        }}
        toggle={() => setNpsModalVisible(false)}
        visible={npsModalVisible}
        userData={userDataNps}
      />
      <PageHeader
        extra={[
          <RequestHistoryButton
            key="requestHistoryButton"
            request_id={request_id}
            user_id={user_id}
          />,
          ...(nextStatus
            ? [
                <ButtonTitled
                  data-test-id="summary-end-accountable"
                  icon="check"
                  key="btnEndAccountable"
                  onClick={() => {
                    if (
                      policyRule &&
                      policyRule.enable_nps &&
                      userDataNps &&
                      userDataNps.request_nps &&
                      !userDataNps.request_nps.request_nps_id
                    ) {
                      setNpsModalVisible(true)
                      return
                    }

                    handleAction()
                  }}
                  size="default"
                  tooltip={
                    enableActions
                      ? 'web.receipts.accept-change'
                      : 'web.receipts.accept'
                  }
                />
              ]
            : []),
          shouldShowRDVButton({
            request: requests.fullRequest,
            userId: user_id
          }) && <RDVButton key="btnRDV" request={requests.fullRequest} />
        ]}
        onBack={() => history.goBack()}
        title={title}
      />
      <ContainerSegmentControl>
        <SegmentControl
          defaultValue="summary"
          onChange={({ target: { value } }) => {
            setCurrent(value)
          }}
          segments={segments}
          value={current}
        />
      </ContainerSegmentControl>
      {segments[current].renderContent()}
    </>
  )
}

export default MyRequestsSummary
